/**
 * PropertyGallery Component
 * 
 * World-class property gallery with professional lightbox viewer
 * Inspired by Sotheby's, Christie's, and Airbnb
 */

'use client';

import type { MediaResponseDto } from '@/lib/api/clients/properties';
import { Box, Images, LayoutGrid } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { PropertyGalleryLightbox } from './PropertyGalleryLightbox';

export interface PropertyGalleryProps {
  media: MediaResponseDto[];
  floorPlans?: MediaResponseDto[];
  models3D?: MediaResponseDto[];
  propertyName: string;
  messages: {
    gallery: {
      title: string;
      subtitle: string;
      viewAll: string;
      noImages: string;
    };
  };
  className?: string;
}

type TabType = 'photos' | 'floorPlans' | 'models3D';

export function PropertyGallery({
  media,
  floorPlans = [],
  models3D = [],
  propertyName,
  messages,
  className = ''
}: PropertyGalleryProps) {
  const [activeTab, setActiveTab] = useState<TabType>('photos');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Get content based on active tab
  const getCurrentContent = () => {
    switch (activeTab) {
      case 'floorPlans':
        return floorPlans;
      case 'models3D':
        return models3D;
      default:
        return media;
    }
  };

  const currentContent = getCurrentContent();

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Check if any content exists
  const hasPhotos = media && media.length > 0;
  const hasFloorPlans = floorPlans && floorPlans.length > 0;
  const hasModels = models3D && models3D.length > 0;
  const hasAnyContent = hasPhotos || hasFloorPlans || hasModels;

  if (!hasAnyContent) {
    return (
      <div className={`font-inter ${className}`}>
        <h2 className="text-xl md:text-2xl font-semibold text-[#222222] mb-2 font-inter">
          {messages.gallery.title}
        </h2>
        <p className="text-[#717171] mb-4">{messages.gallery.subtitle}</p>
        <p className="text-[#717171] italic">{messages.gallery.noImages}</p>
      </div>
    );
  }

  return (
    <div className={`font-inter ${className}`}>
      <div className="flex justify-between items-start mb-8">
        <div className="flex items-start gap-3">
          <div className="w-1 h-8 bg-gradient-to-b from-[#BBA339] to-[#E4CE4A] rounded-full"></div>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#222222] mb-1 font-inter">
              {messages.gallery.title}
            </h2>
            <p className="text-[#717171] text-base">{messages.gallery.subtitle}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('photos')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-all relative ${
            activeTab === 'photos'
              ? 'text-[#222222]'
              : 'text-[#717171] hover:text-[#222222]'
          }`}
        >
          <Images size={18} />
          Photos {hasPhotos && `(${media.length})`}
          {activeTab === 'photos' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#BBA339] to-[#E4CE4A]"></div>
          )}
        </button>
        
        {hasFloorPlans && (
          <button
            onClick={() => setActiveTab('floorPlans')}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-all relative ${
              activeTab === 'floorPlans'
                ? 'text-[#222222]'
                : 'text-[#717171] hover:text-[#222222]'
            }`}
          >
            <LayoutGrid size={18} />
            Floor Plans ({floorPlans.length})
            {activeTab === 'floorPlans' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#BBA339] to-[#E4CE4A]"></div>
            )}
          </button>
        )}
        
        {hasModels && (
          <button
            onClick={() => setActiveTab('models3D')}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-all relative ${
              activeTab === 'models3D'
                ? 'text-[#222222]'
                : 'text-[#717171] hover:text-[#222222]'
            }`}
          >
            <Box size={18} />
            3D Models ({models3D.length})
            {activeTab === 'models3D' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#BBA339] to-[#E4CE4A]"></div>
            )}
          </button>
        )}
      </div>
      
      {/* Content Grid - Professional Layout */}
      {currentContent.length > 0 ? (
        <>
          {/* Hero Grid Layout - First image is large */}
          {currentContent.length >= 5 ? (
            <div className="grid grid-cols-4 gap-2 md:gap-3 mb-3">
              {/* Large Featured Image */}
              <div 
                onClick={() => openLightbox(0)}
                className="col-span-4 md:col-span-2 md:row-span-2 relative aspect-video md:aspect-[4/3] overflow-hidden rounded-xl cursor-pointer group bg-gray-100 shadow-md hover:shadow-xl transition-all"
              >
                <Image
                  src={currentContent[0]?.url || ''}
                  alt={`${propertyName} - Featured Image`}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  priority
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
                <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-semibold">
                  Featured
                </div>
              </div>

              {/* Smaller Images Grid */}
              {currentContent.slice(1, 5).map((item, index) => (
                <div 
                  key={item.id}
                  onClick={() => openLightbox(index + 1)}
                  className="col-span-2 md:col-span-1 relative aspect-square overflow-hidden rounded-xl cursor-pointer group bg-gray-100 shadow-md hover:shadow-xl transition-all"
                >
                  <Image
                    src={item.url}
                    alt={`${propertyName} - Image ${index + 2}`}
                    fill
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                    sizes="(max-width: 768px) 50vw, 25vw"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
                </div>
              ))}
            </div>
          ) : (
            /* Fallback simple grid for less than 5 images */
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3 mb-3">
              {currentContent.slice(0, 6).map((item, index) => (
                <div 
                  key={item.id}
                  onClick={() => openLightbox(index)}
                  className="relative aspect-square overflow-hidden rounded-xl cursor-pointer group bg-gray-100 shadow-md hover:shadow-xl transition-all"
                >
                  <Image
                    src={item.url}
                    alt={`${propertyName} - Image ${index + 1}`}
                    fill
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                    sizes="(max-width: 768px) 50vw, 33vw"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
                </div>
              ))}
            </div>
          )}

          {/* View All Button - Only if more than 5 images */}
          {currentContent.length > 5 && (
            <button
              onClick={() => openLightbox(5)}
              className="w-full py-4 px-6 bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-[#BBA339] rounded-xl text-[#222222] font-semibold transition-all hover:shadow-md flex items-center justify-center gap-2"
            >
              <Images size={20} />
              {messages.gallery.viewAll} ({currentContent.length} {activeTab === 'floorPlans' ? 'Floor Plans' : activeTab === 'models3D' ? 'Models' : 'Photos'})
            </button>
          )}

          {/* Lightbox */}
          <PropertyGalleryLightbox
            images={currentContent}
            initialIndex={lightboxIndex}
            isOpen={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
            propertyName={`${propertyName} - ${activeTab === 'floorPlans' ? 'Floor Plans' : activeTab === 'models3D' ? '3D Models' : 'Photos'}`}
          />
        </>
      ) : (
        <p className="text-[#717171] italic py-8 text-center bg-gray-50 rounded-xl">{messages.gallery.noImages}</p>
      )}
    </div>
  );
}
